import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import {
    getFormValidationAdornment,
    MagnifyingGlassIcon,
    MapPinIcon,
    TextField,
    Typography,
} from '@eventbrite/marmalade';
import * as Popover from '@radix-ui/react-popover';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';
import { BREAKPOINTS, colors } from '../config';
import { PoweredByGoogleLogo } from './PoweredByGoogleLogo';
import type { Place, VenueValues } from './types';
import { VenueType } from './types';
import { useCountries } from './useCountries';
import { useHighlightList } from './useHighlightList';
import { useVenueSearch } from './useVenueSearch';
import {
    createVenueFromGooglePlace,
    createVenueFromOrgVenue,
} from './utils/places';
import { isVenueComplete } from './utils/venues';

export enum VenueLocationTestId {
    Container = 'VenueLocationContainer',
    Field = 'VenueLocationField',
    NotFound = 'VenueLocationNotFound',
    Content = 'VenueLocationContent',
}

export interface VenueLocationProps
    extends Omit<React.ComponentPropsWithRef<typeof TextField>, 'onError'> {
    onSelectVenue: (venue: VenueValues) => void;
    onResetVenue?: () => void;
    organizationId: string;
    onError?: ({ type, message }: { type: string; message: string }) => void;
    onNoResults?: () => void;
    error?: any;
    /**
     * ISO 3166-1 Alpha-2 country codes for countries that should be filtered
     * from autocomplete results. This prop shadows a feature flag in the consumer app.
     * Example: new Set(['BR']) to filter Brazil.
     */
    blacklistedCountries?: Set<string>;
}

export const VENUE_ERROR_TYPE = {
    MISSING_REQUIRED_FIELDS: 'MISSING_REQUIRED_FIELDS',
    NOT_FOUND: 'NOT_FOUND',
};

export const VenueLocation = React.forwardRef<
    HTMLInputElement,
    VenueLocationProps
>(
    (
        {
            onChange = defaultOnChange,
            onResetVenue = defaultOnReset,
            onSelectVenue,
            organizationId,
            onError,
            error,
            value,
            label,
            onNoResults,
            blacklistedCountries,
        },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const { getSuggestions, suggestions, getSuggestion } = useVenueSearch({
            organizationId,
            blacklistedCountries: blacklistedCountries ?? new Set(),
        });
        const { data: countries = [] } = useCountries(
            5000,
            blacklistedCountries ?? new Set(),
        );
        const [containerWidth, setContainerWidth] = useState(0);
        const closeSuggestions = () => setOpen(false);
        const openSuggestions = () => setOpen(true);
        const closeSuggestionsAndReset = () => {
            onResetVenue();
            closeSuggestions();
        };
        const [open, setOpen] = useState(false);
        const isMounted = useMountedState();
        const {
            highlightNext,
            highlightPrevious,
            hightlighted,
            reset: resetHighlight,
        } = useHighlightList(suggestions);

        const onClick = async ({ type, value, data }: Place) => {
            closeSuggestions();

            if (type === VenueType.Venue) {
                try {
                    const isComplete = isVenueComplete(data, countries);
                    onSelectVenue(createVenueFromOrgVenue(data));

                    if (!isComplete) {
                        throw new Error('Venue is not complete');
                    }
                } catch (e) {
                    if (onError) {
                        onError({
                            type: VENUE_ERROR_TYPE.MISSING_REQUIRED_FIELDS,
                            message: 'Venue is not complete',
                        });
                    }
                }
            }

            if (type === VenueType.Google) {
                try {
                    const place = await getSuggestion({ placeId: value });
                    if (place === null) {
                        throw new Error('Place not found');
                    }

                    onSelectVenue(createVenueFromGooglePlace(place));
                } catch (e) {
                    if (onError) {
                        onError({
                            type: VENUE_ERROR_TYPE.NOT_FOUND,
                            message:
                                'We could not find the location you entered. Please try again.',
                        });
                    }
                }
            }
        };

        const hotkeyshandler = {
            ArrowDown: () => {
                if (open) {
                    highlightNext();
                } else {
                    openSuggestions();
                }
            },
            ArrowUp: highlightPrevious,
            Enter: async () => {
                const selection = suggestions[hightlighted];
                if (selection) {
                    await onClick(selection);
                }
            },
            Escape: closeSuggestionsAndReset,
        };

        const handleFieldClick = () => {
            if (!open) {
                openSuggestions();
            }
        };

        const handlePointerDownOutside = (
            event: CustomEvent<{ originalEvent: PointerEvent }>,
        ) => {
            const target = event.detail.originalEvent.target as Element;
            if (!containerRef.current?.contains(target)) {
                closeSuggestionsAndReset();
            }
        };

        const handleKeyDown = async (
            event: React.KeyboardEvent<HTMLInputElement>,
        ): Promise<void> => {
            const { key } = event;

            // Stop propagation only if dropdown is open for Escape
            if (key === 'Escape' && open) {
                event.stopPropagation();
            }

            if (key in hotkeyshandler) {
                event.preventDefault();
                const handler =
                    hotkeyshandler[key as keyof typeof hotkeyshandler];
                await handler();
            } else {
                openSuggestions();
            }
        };

        const calculatePopoverWidth = useCallback(() => {
            const width = containerRef.current?.clientWidth || 0;
            if (isMounted()) {
                setContainerWidth(width);
            }
        }, [isMounted]);

        useEffect(() => {
            calculatePopoverWidth();
            // Need to copy the ref into another variable to overcome the issue
            // with react cleaning up the ref when unmounting the component.
            const containerElementCopy = containerRef.current;

            const observer = new ResizeObserver(calculatePopoverWidth);
            if (containerElementCopy) {
                observer.observe(containerElementCopy);
            }

            return () => {
                observer.disconnect();
            };
        }, [containerRef, calculatePopoverWidth]);

        return (
            <Container
                data-testid={VenueLocationTestId.Container}
                ref={containerRef}
                aria-expanded={open}
                aria-owns="search-listbox"
                aria-haspopup="listbox"
            >
                <Popover.Root open={open}>
                    <Popover.Trigger asChild>
                        <TextField
                            prefix={
                                <MagnifyingGlassIcon
                                    color={colors['ui-700']}
                                    size="small"
                                />
                            }
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={open}
                            aria-controls="search-listbox"
                            aria-label={label}
                            data-testid={VenueLocationTestId.Field}
                            id={VenueLocationTestId.Field}
                            label={label}
                            type="text"
                            autoComplete="off"
                            hasError={!!error}
                            helperText={
                                <span role="alert" aria-live="assertive">
                                    {error?.message}
                                </span>
                            }
                            validationAdornment={getFormValidationAdornment({
                                hasError: !!error,
                            })}
                            ref={ref}
                            onKeyDown={handleKeyDown}
                            onClick={handleFieldClick}
                            value={value || ''}
                            onChange={(value, name, event) => {
                                getSuggestions(value);
                                resetHighlight();
                                onChange(value, name, event);
                            }}
                            required
                        />
                    </Popover.Trigger>
                    <Popover.Portal>
                        <PopoverContent
                            align="start"
                            onOpenAutoFocus={focusTrap}
                            onPointerDownOutside={handlePointerDownOutside}
                            data-testid={VenueLocationTestId.Content}
                            style={{
                                width: containerWidth,
                            }}
                        >
                            <Suggestions id="search-listbox" role="listbox">
                                {suggestions.map((suggestion, index) => {
                                    const isHighlighted =
                                        index === hightlighted;
                                    return (
                                        <Suggestion
                                            key={suggestion.value}
                                            role="option"
                                            aria-selected={isHighlighted}
                                            aria-label={suggestion.content}
                                            onClick={() => onClick(suggestion)}
                                        >
                                            <MapPinIcon
                                                color={
                                                    isHighlighted
                                                        ? colors['ui-100']
                                                        : colors['ui-700']
                                                }
                                            />
                                            <Typography
                                                variant="body-md"
                                                style={{
                                                    color: isHighlighted
                                                        ? colors['ui-100']
                                                        : colors['ui-700'],
                                                }}
                                            >
                                                {suggestion.content}
                                            </Typography>
                                        </Suggestion>
                                    );
                                })}

                                {suggestions.length === 0 && (
                                    <Suggestion
                                        role="option"
                                        aria-selected={false}
                                        aria-label={noResultLabel}
                                        data-testid={
                                            VenueLocationTestId.NotFound
                                        }
                                        onClick={onNoResults}
                                    >
                                        <Typography
                                            variant="body-md-bold"
                                            style={{
                                                color: colors.control,
                                            }}
                                        >
                                            {noResultLabel}
                                        </Typography>
                                    </Suggestion>
                                )}
                            </Suggestions>
                            <Logo>
                                <PoweredByGoogleLogo />
                            </Logo>
                        </PopoverContent>
                    </Popover.Portal>
                </Popover.Root>
            </Container>
        );
    },
);

const noResultLabel = gettext(
    "Can't find what you're looking for? Add a new venue or address.",
).toString();

const focusTrap = (e: Event) => e.preventDefault();
const defaultOnChange = (value: string) => {
    console.log(value);
};
// eslint-disable-next-line @typescript-eslint/no-empty-function
const defaultOnReset = () => {};

const Container = styled.div`
    position: relative;
`;

// The popover relays on the input text field position
// as we are using it as a trigger. To position the popover
// correctly we need to calculate the position offset of the
// input field. That is why we are using magic numbers in the
// transform property.
const PopoverContent = styled(Popover.Content)`
    background-color: ${colors.white};
    position: relative;
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 24px 0 rgba(40, 44, 53, 0.1),
        0 2px 8px 0 rgba(40, 44, 53, 0.1);
    border-radius: 0.5rem;
    overflow: hidden;
    margin-top: 0.5rem;
    transform: translateX(-2.375rem);
    z-index: 100;

    @media (min-width: ${BREAKPOINTS.SMALL_WIDE}px) {
        transform: translateX(-2.625rem);
    }
`;

const Suggestions = styled.div``;

const Suggestion = styled.div`
    padding: 0 1rem;
    display: flex;
    gap: 8px;
    align-items: center;
    cursor: pointer;
    background-color: ${(props) =>
        props['aria-selected'] ? colors.control : colors.white};
    height: 3.25rem;
    border-bottom: 1px solid ${colors['ui-200']};

    &:last-child {
        border-bottom: none;
    }
`;

const Logo = styled.div`
    display: flex;
    justify-content: end;
    padding: 0.5rem;
    align-items: end;
    background-color: ${colors.white};
`;
